import type { AthleteGoal, BlockPhase, RecoveryContext } from '@cadencia/contracts';
import {
  firstBlockPhase,
  isMinor,
  MENSAJE_FUERA_DE_ALCANCE,
  populationScope,
  type CoachAIProvider,
  type OutOfScopeReason,
  type PopulationSituation,
} from '@cadencia/domain';
import { leerBloqueActivo } from '../bloqueActivo.js';
import { saveMeasurement, saveWeight } from '../repositories/progressRepo.js';
import {
  getPreferences,
  getProfile,
  savePreferences,
  saveProfile,
  type ProfileInput,
} from '../repositories/profileRepo.js';
import { createBlockForPhase } from '../repositories/trainingRepo.js';
import type { WriteContext } from '../writes.js';
import { proponerBloque, type PropuestaDeBloque } from './blockProposalService.js';

/**
 * Cerrar el test inicial (docs/TEST_INICIAL.md §2.5): guardar lo contestado y,
 * si el atleta no tiene plan todavía, crear el primero.
 *
 * EL ORDEN IMPORTA. El perfil y las preferencias se guardan ANTES de crear el
 * bloque, porque `createBlockForPhase` y `proposeBlock` los LEEN —días,
 * minutos, limitaciones— de la base local. Crear el bloque primero lo haría con
 * el perfil de antes, o sin ninguno.
 *
 * Y todo es local: guardar nunca espera a la red. Lo único que puede esperar es
 * la propuesta de la IA, y solo si se ha pedido; si no contesta, sale el bloque
 * determinista y se dice (`disenoConIA` a `false`).
 */

export interface PuntoDePartida {
  pesoKg: number | null;
  cinturaCm: number | null;
  brazoCm: number | null;
  musloCm: number | null;
}

export interface CierreDelTest {
  perfil: ProfileInput;
  objetivo: AthleteGoal;
  recuperacion: RecoveryContext;
  partida: PuntoDePartida;
  /** Lunes en el que empieza el primer bloque. */
  inicio: string;
  hoy: string;
  /** `null` = sin IA, o no se quiere usar. */
  ia: CoachAIProvider | null;
  /**
   * Situaciones fuera del alcance general y el visto bueno profesional
   * (safety/population.ts). Deciden si se crea el plan; no se guardan.
   */
  situaciones: readonly PopulationSituation[];
  vistoBueno: boolean;
}

export interface ResultadoDelTest {
  /** `null` si no se creó bloque: el atleta ya tenía uno (repetía el test). */
  fase: BlockPhase | null;
  /** Avisos del motor al construir el bloque: huecos, recortes por tiempo… */
  avisos: string[];
  /** Si el bloque lo diseñó la IA. `false` también si se pidió y no contestó. */
  disenoConIA: boolean;
  /** La propuesta de la IA, para enseñar su razonamiento. `null` sin IA. */
  propuesta: PropuestaDeBloque | null;
  /**
   * Si se pidió a la IA y el bloque no lo diseñó ella, por qué. `null` si no se
   * pidió o si lo diseñó. Se dice en pantalla: «presupuesto agotado» y «sin
   * red» no se arreglan igual (24/09/2026).
   */
  motivoSinIA: string | null;
  /**
   * Por qué NO se creó el plan cuando la persona queda fuera del alcance
   * general. Vacío si se creó, o si no se creó por otro motivo (ya tenía uno).
   */
  fueraDeAlcance: OutOfScopeReason[];
}

export async function cerrarTestInicial(
  ctx: WriteContext,
  cierre: CierreDelTest,
): Promise<ResultadoDelTest> {
  await saveProfile(ctx, cierre.perfil);
  await savePreferences(ctx, {
    trainingGoal: cierre.objetivo,
    recoveryContext: cierre.recuperacion,
  });

  // La línea base con la que se medirá la evolución. Opcional, y cada dato por
  // su lado: quien no tiene cinta métrica no se queda sin su peso de partida.
  if (cierre.partida.pesoKg !== null) {
    await saveWeight(ctx, {
      measuredOn: cierre.hoy,
      weightKg: cierre.partida.pesoKg,
      bodyFatPct: null,
      conditionsStandard: true,
      note: 'Punto de partida (test inicial)',
    });
  }
  const { cinturaCm, brazoCm, musloCm } = cierre.partida;
  if (cinturaCm !== null || brazoCm !== null || musloCm !== null) {
    await saveMeasurement(ctx, {
      measuredOn: cierre.hoy,
      waistCm: cinturaCm,
      chestCm: null,
      armCm: brazoCm,
      thighCm: musloCm,
      calfCm: null,
      side: null,
      note: 'Punto de partida (test inicial)',
    });
  }

  const alcance = populationScope({
    birthDate: cierre.perfil.birthDate,
    today: cierre.hoy,
    situations: cierre.situaciones,
    professionalClearance: cierre.vistoBueno,
  });
  if (!alcance.planAllowed) {
    return {
      fase: null,
      avisos: alcance.reasons.map((m) => MENSAJE_FUERA_DE_ALCANCE[m]),
      disenoConIA: false,
      propuesta: null,
      motivoSinIA: null,
      fueraDeAlcance: alcance.reasons,
    };
  }

  const resultado = await crearPrimerBloque(ctx, { inicio: cierre.inicio, hoy: cierre.hoy, ia: cierre.ia });
  // Con visto bueno, el plan es el GENERAL, y se dice: adaptarlo a esa
  // situación es cosa del profesional que lo ha autorizado.
  if (resultado.fase !== null && alcance.reasons.length > 0) {
    resultado.avisos.unshift(
      'Este plan es el general para adultos sanos. Enséñaselo a quien te ha dado el visto bueno y ajusta lo que te diga; las limitaciones se cambian desde Más → Perfil.',
    );
  }
  return resultado;
}

/**
 * El primer bloque, con la fase que sale de lo declarado (`firstBlockPhase`).
 *
 * Lo usan el test y Entrenar —cuando el test se hizo sin gimnasio y el plan se
 * crea después—, y por eso lee el perfil y las preferencias GUARDADOS en vez
 * de recibirlos: dos caminos que decidieran la fase cada uno con sus datos
 * acabarían creando bloques distintos para la misma persona.
 *
 * No crea nada si ya hay bloque, ni si todavía no se sabe si lo hay: crearlo en
 * esa ventana es justo lo que duplicó los bloques el 22/09/2026.
 */
export async function crearPrimerBloque(
  ctx: WriteContext,
  opciones: { inicio: string; hoy: string; ia: CoachAIProvider | null },
): Promise<ResultadoDelTest> {
  const estado = await leerBloqueActivo(ctx.userId);
  if (!estado.conocido || estado.bloque !== null) {
    return { fase: null, avisos: [], disenoConIA: false, propuesta: null, motivoSinIA: null, fueraDeAlcance: [] };
  }

  const perfil = await getProfile(ctx.userId);
  // Un menor no recibe plan automático por NINGÚN camino: este lo usan el test
  // y Entrenar, y la edad sí está guardada (la fecha de nacimiento).
  if (isMinor(perfil?.birthDate ?? null, opciones.hoy)) {
    return {
      fase: null,
      avisos: [MENSAJE_FUERA_DE_ALCANCE.minor],
      disenoConIA: false,
      propuesta: null,
      motivoSinIA: null,
      fueraDeAlcance: ['minor'],
    };
  }
  const preferencias = await getPreferences(ctx.userId);
  const decision = firstBlockPhase({
    detrainedSince: perfil?.detrainedSince ?? null,
    goal: preferencias?.trainingGoal ?? 'muscle_gain',
    recovery: preferencias?.recoveryContext ?? 'normal',
    hasPriorityMuscles: false,
    today: opciones.hoy,
  });

  const pedida =
    opciones.ia !== null
      ? await proponerBloque(opciones.ia, ctx.userId, decision.phase, {
          phaseReasonCodes: decision.reasonCodes,
        })
      : null;
  const propuesta = pedida?.propuesta ?? null;
  const motivoSinIA =
    pedida === null
      ? null
      : propuesta === null
        ? pedida.motivo
        : propuesta.sessionPlan === null
          ? 'la estructura que propuso no pasó las reglas del motor'
          : null;

  // Otra vez, DESPUÉS de la IA: la propuesta tarda decenas de segundos y en
  // ese tiempo la bajada puede traer el bloque que otro dispositivo creó. Una
  // respuesta sobre un estado viejo no crea un segundo bloque (24/09/2026).
  const despues = await leerBloqueActivo(ctx.userId);
  if (!despues.conocido || despues.bloque !== null) {
    return { fase: null, avisos: [], disenoConIA: false, propuesta, motivoSinIA, fueraDeAlcance: [] };
  }

  const creado = await createBlockForPhase(ctx, decision.phase, {
    startDate: opciones.inicio,
    ...(propuesta
      ? {
          weeks: propuesta.weeks,
          volumeTargets: propuesta.targets,
          ...(propuesta.sessionPlan ? { sessionPlan: propuesta.sessionPlan } : {}),
        }
      : {}),
  });

  return {
    fase: decision.phase,
    avisos: creado.warnings,
    disenoConIA: propuesta?.sessionPlan != null,
    propuesta,
    motivoSinIA,
    fueraDeAlcance: [],
  };
}
